import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { toast } from '#/utils/toast';

/**
 * 單據備註專頁共用儲存 hook（勿寫死入庫）。
 * 轉倉／調整後續接：傳入對應 fetchDetail／updateRemark／queryKey。
 *
 * @param {object} options
 * @param {string} options.id
 * @param {Function} options.fetchDetail
 * @param {Function} options.updateRemark
 * @param {Function} options.detailQueryKey
 * @param {Function} options.listQueryKeyPrefix
 * @param {Function} options.getErrorMessage
 * @param {Function} [options.isVersionConflict]
 * @param {string|Function} options.afterSaveNavigateTo - 成功後導向（通常 detail）
 */
function useDocumentRemarkSave({
  id,
  fetchDetail,
  updateRemark,
  detailQueryKey,
  listQueryKeyPrefix,
  getErrorMessage,
  isVersionConflict,
  afterSaveNavigateTo,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: detailQueryKey(id),
    queryFn: () => fetchDetail(id),
    enabled: Boolean(id),
  });

  const detail = detailQuery.data?.data;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt ?? null;

  const saveMutation = useMutation({
    mutationFn: (remark) => updateRemark(id, {
      remark: remark || null,
      updatedAt,
    }),
    onSuccess: async (result) => {
      toast.success(result?.message || '備註已更新');
      await queryClient.invalidateQueries({ queryKey: listQueryKeyPrefix() });
      if (id) {
        const key = detailQueryKey(id);
        await queryClient.invalidateQueries({ queryKey: key });
        await queryClient.refetchQueries({ queryKey: key });
      }
      const target = typeof afterSaveNavigateTo === 'function'
        ? afterSaveNavigateTo(id, result)
        : afterSaveNavigateTo;
      if (target) {
        navigate(target);
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage?.(error) ?? '更新備註失敗');
      if (isVersionConflict?.(error)) {
        detailQuery.refetch();
      }
    },
  });

  function saveRemark(remark) {
    if (saveMutation.isPending) {
      return;
    }
    saveMutation.mutate(remark);
  }

  return {
    detail,
    detailQuery,
    isPending: saveMutation.isPending,
    saveRemark,
  };
}

export default useDocumentRemarkSave;
